import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL mutation for removing game assignment
export const REMOVE_GAME_ASSIGNMENT_MUTATION = gql`
  mutation RemoveGameAssignment($input: RemoveGameFromLabelInput!) {
    removeGameAssignment(input: $input) {
      id
      name
      gameId
      providerId
      categoryId
      launchUrl
      demoAvailable
      freespinAvailable
      deviceType
      betType
      linesCount
      isActive
      iconUrl
      description
      createdAt
      position
    }
  }
`;

// Types for the remove game assignment input
export interface RemoveGameFromLabelInput {
  gameId: number;
  environmentId: number;
  labelId: number;
}

// Raw GraphQL response interface (before transformation)
export interface RawGame {
  id: string; // GraphQL ID fields are strings
  name: string;
  gameId: string;
  providerId: number;
  categoryId: number;
  launchUrl?: string | null;
  demoAvailable: boolean;
  freespinAvailable: boolean;
  deviceType: string;
  betType?: string | null;
  linesCount?: number | null;
  isActive: boolean;
  iconUrl?: string | null;
  description?: string | null;
  createdAt: string;
  position: number;
}

// Transformed Game interface (after conversion)
export interface Game {
  id: number;
  name: string;
  gameId: string;
  providerId: number;
  categoryId: number;
  launchUrl?: string | null;
  demoAvailable: boolean;
  freespinAvailable: boolean;
  deviceType: string;
  betType?: string | null;
  linesCount?: number | null;
  isActive: boolean;
  iconUrl?: string | null;
  description?: string | null;
  createdAt: string;
  position: number;
}

export interface RemoveGameAssignmentResponse {
  removeGameAssignment: RawGame;
}

// RemoveGameAssignment service class
export class RemoveGameAssignmentService {
  private static instance: RemoveGameAssignmentService;

  private constructor() {}

  public static getInstance(): RemoveGameAssignmentService {
    if (!RemoveGameAssignmentService.instance) {
      RemoveGameAssignmentService.instance = new RemoveGameAssignmentService();
    }
    return RemoveGameAssignmentService.instance;
  }

  // Remove game assignment
  async removeGameAssignment(input: RemoveGameFromLabelInput): Promise<Game> {
    try {
      const { data } = await apolloClient.mutate<RemoveGameAssignmentResponse>({
        mutation: REMOVE_GAME_ASSIGNMENT_MUTATION,
        variables: { input },
      });

      if (!data?.removeGameAssignment) {
        throw new Error(
          'No data returned from remove game assignment mutation',
        );
      }

      // Transform the response to convert string ID to number
      const transformedGame = {
        ...data.removeGameAssignment,
        id: Number(data.removeGameAssignment.id),
      };

      return transformedGame;
    } catch (error: any) {
      console.error('Error removing game assignment:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }
}

// Export singleton instance
export const removeGameAssignmentService =
  RemoveGameAssignmentService.getInstance();

// Export convenience function
export async function removeGameAssignment(
  input: RemoveGameFromLabelInput,
): Promise<Game> {
  return removeGameAssignmentService.removeGameAssignment(input);
}
